import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Search, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatPrice } from '../../lib/utils';

export default function AdminTrending() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/products/all`);
      // Normalize product IDs
      const productsWithId = response.data.map(p => ({
        ...p,
        id: p.id || p._id
      }));
      setProducts(productsWithId);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const toggleTrending = async (product, currentStatus) => {
    const productId = product.id || product._id;
    try {
      await axiosInstance.put(`${API_URL}/api/admin/products/${productId}/trending`, null, {
        params: { is_trending: !currentStatus }
      });
      toast.success(`Product ${!currentStatus ? 'added to' : 'removed from'} trending`);
      fetchProducts();
    } catch (error) {
      console.error('Trending toggle error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update trending status');
    }
  };

  const filteredProducts = products.filter(product =>
    !searchQuery || 
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Trending Products</h1>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Select products to feature on homepage</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => {
            const productId = product.id || product._id;
            return (
              <Card key={productId} className={product.is_trending ? 'border-2 border-yellow-400' : ''}>
                <CardContent className="p-4">
                  <div className="aspect-square mb-4 rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={product.images?.[0] || 'https://via.placeholder.com/300x300/E8F5E9/2E7D32?text=No+Image'} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-lg">{formatPrice(product.sale_price || product.price)}</span>
                    <span className="text-xs text-gray-500">{product.sku}</span>
                  </div>
                  <Button
                    onClick={() => toggleTrending(product, product.is_trending)}
                    className={`w-full ${product.is_trending ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    {product.is_trending ? 'Remove from Trending' : 'Mark as Trending'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No products found</p>
          </div>
        )}
      </div>
    </div>
  );
}
